import React, { useCallback, useMemo, useState } from "react";
import type { PlatformCustomCoverDetail } from "@/partner/entities/platforms";
import { DEVICE_MODES, DEFAULT_ALIGN } from "../model/constants";
import { CustomCoverContext } from "./CustomCoverContext";
import type {
  DeviceMode,
  CoverText,
  CoverButton,
  CoverBackgrounds,
  ActiveBackground,
  CustomCoverState,
  CustomCoverContextValue,
} from "../model/types";

const getInitialState = (): CustomCoverState => ({
  selectedItem: null,
  coverId: null,
  title: {},
  subTitle: {},
  buttons: [],
  drawerOpen: false,
  deviceMode: DEVICE_MODES.mobile,
  backgrounds: {},
  brightness: null,
  publishAt: null,
  buttonsColor: null,
  buttonsSize: null,
  align: DEFAULT_ALIGN,
  alignMobile: DEFAULT_ALIGN,
  verticalAlign: DEFAULT_ALIGN,
  verticalAlignMobile: DEFAULT_ALIGN,
});

export const CustomCoverProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialState = getInitialState();

  const [selectedItem, setSelectedItem] = useState<string | null>(
    initialState.selectedItem
  );
  const [coverId, setCoverId] = useState<string | null>(initialState.coverId);
  const [title, setTitle] = useState<CoverText>(initialState.title);
  const [subTitle, setSubTitle] = useState<CoverText>(initialState.subTitle);
  const [buttons, setButtons] = useState<CoverButton[]>(initialState.buttons);
  const [drawerOpen, setDrawerOpen] = useState(initialState.drawerOpen);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>(
    initialState.deviceMode
  );
  const [backgrounds, setBackgrounds] = useState<CoverBackgrounds>(
    initialState.backgrounds
  );
  const [brightness, setBrightness] = useState<number | null>(
    initialState.brightness
  );
  const [publishAt, setPublishAt] = useState<string | null>(
    initialState.publishAt
  );
  const [buttonsColor, setButtonsColor] = useState<string | null>(
    initialState.buttonsColor
  );
  const [buttonsSize, setButtonsSize] = useState<string | null>(
    initialState.buttonsSize
  );
  const [align, setAlign] = useState(initialState.align);
  const [alignMobile, setAlignMobile] = useState(initialState.alignMobile);
  const [verticalAlign, setVerticalAlign] = useState(
    initialState.verticalAlign
  );
  const [verticalAlignMobile, setVerticalAlignMobile] = useState(
    initialState.verticalAlignMobile
  );

  const activeBackgrounds = useMemo<ActiveBackground | null>(() => {
    const isMobile = deviceMode === DEVICE_MODES.mobile;
    const videoId = isMobile
      ? backgrounds.mobileVideoId
      : backgrounds.desktopVideoId;
    const imageId = isMobile
      ? backgrounds.mobileImageId
      : backgrounds.desktopImageId;

    if (videoId) {
      return { type: "video", id: videoId };
    }
    if (imageId) {
      return { type: "image", id: imageId };
    }
    return null;
  }, [backgrounds, deviceMode]);

  const handleSetupSelectedCover = useCallback(
    (data: PlatformCustomCoverDetail | null) => {
      if (!data) {
        return;
      }

      const state = getInitialState();

      setTitle(data.title || state.title);
      setSubTitle(data.subTitle || state.subTitle);
      setButtons(data.buttons || state.buttons);
      setCoverId(data.id || state.coverId);
      setBrightness(data.brightness ?? state.brightness);
      setBackgrounds(data.background || state.backgrounds);
      setPublishAt(data.publishAt || state.publishAt);
      setButtonsColor(data.buttonsColor || state.buttonsColor);
      setButtonsSize(data.buttonsSize || state.buttonsSize);
      setAlign(data.align || state.align);
      setAlignMobile(data.alignMobile || state.alignMobile);
      setVerticalAlign(data.verticalAlign || state.verticalAlign);
      setVerticalAlignMobile(
        data.verticalAlignMobile || state.verticalAlignMobile
      );
    },
    []
  );

  const handleResetCoverState = useCallback(() => {
    const state = getInitialState();
    setTitle(state.title);
    setSubTitle(state.subTitle);
    setButtons(state.buttons);
    setCoverId(state.coverId);
    setDrawerOpen(state.drawerOpen);
    setDeviceMode(state.deviceMode);
    setSelectedItem(state.selectedItem);
    setBackgrounds(state.backgrounds);
    setBrightness(state.brightness);
    setPublishAt(state.publishAt);
    setButtonsColor(state.buttonsColor);
    setButtonsSize(state.buttonsSize);
    setAlign(state.align);
    setAlignMobile(state.alignMobile);
    setVerticalAlign(state.verticalAlign);
    setVerticalAlignMobile(state.verticalAlignMobile);
  }, []);

  const handleChangeTitle = useCallback((input: Partial<CoverText>) => {
    setTitle((prev) => ({ ...prev, ...input }));
  }, []);

  const handleChangeSubTitle = useCallback((input: Partial<CoverText>) => {
    setSubTitle((prev) => ({ ...prev, ...input }));
  }, []);

  const handleChangeButtons = useCallback(
    (input: { button: number; values: Partial<CoverButton> }) => {
      setButtons((prev) =>
        prev.map((btn, index) =>
          index === input.button ? { ...btn, ...input.values } : btn
        )
      );
    },
    []
  );

  const handleChangeBackground = useCallback(
    (input: { variant: keyof CoverBackgrounds; fileId: string | null }) => {
      setBackgrounds((prev) => ({
        ...prev,
        [input.variant]: input.fileId || null,
      }));
    },
    []
  );

  const handleToggleDeviceMode = useCallback(() => {
    setDeviceMode((prev) =>
      prev === DEVICE_MODES.mobile ? DEVICE_MODES.desktop : DEVICE_MODES.mobile
    );
  }, []);

  const handleChangeSelectedItem = useCallback((element: string) => {
    setSelectedItem(element);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedItem(null);
    setDrawerOpen(false);
  }, []);

  // Memoize simple setters
  const handleChangeBrightness = useCallback((value: number | null) => {
    setBrightness(value);
  }, []);

  const handleChangePublishAt = useCallback((value: string | null) => {
    setPublishAt(value);
  }, []);

  const handleChangeButtonsColor = useCallback((value: string | null) => {
    setButtonsColor(value);
  }, []);

  const handleChangeButtonsSize = useCallback((value: string | null) => {
    setButtonsSize(value);
  }, []);

  const handleChangeAlign = useCallback((value: string) => {
    setAlign(value);
  }, []);

  const handleChangeAlignMobile = useCallback((value: string) => {
    setAlignMobile(value);
  }, []);

  const handleChangeVerticalAlign = useCallback((value: string) => {
    setVerticalAlign(value);
  }, []);

  const handleChangeVerticalAlignMobile = useCallback((value: string) => {
    setVerticalAlignMobile(value);
  }, []);

  const value = useMemo<CustomCoverContextValue>(
    () => ({
      selectedItem,
      coverId,
      title,
      subTitle,
      buttons,
      drawerOpen,
      deviceMode,
      backgrounds,
      activeBackgrounds,
      brightness,
      publishAt,
      buttonsColor,
      buttonsSize,
      align,
      alignMobile,
      verticalAlign,
      verticalAlignMobile,
      onChangeSelectedItem: handleChangeSelectedItem,
      onCloseDrawer: handleCloseDrawer,
      onToggleDeviceMode: handleToggleDeviceMode,
      onChangeTitle: handleChangeTitle,
      onChangeSubTitle: handleChangeSubTitle,
      onChangeButtons: handleChangeButtons,
      onSetupSelectedCover: handleSetupSelectedCover,
      onResetCoverState: handleResetCoverState,
      onChangeBackground: handleChangeBackground,
      onChangeBrightness: handleChangeBrightness,
      onChangePublishAt: handleChangePublishAt,
      onChangeButtonsColor: handleChangeButtonsColor,
      onChangeButtonsSize: handleChangeButtonsSize,
      onChangeAlign: handleChangeAlign,
      onChangeAlignMobile: handleChangeAlignMobile,
      onChangeVerticalAlign: handleChangeVerticalAlign,
      onChangeVerticalAlignMobile: handleChangeVerticalAlignMobile,
    }),
    [
      selectedItem,
      coverId,
      title,
      subTitle,
      buttons,
      drawerOpen,
      deviceMode,
      backgrounds,
      activeBackgrounds,
      brightness,
      publishAt,
      buttonsColor,
      buttonsSize,
      align,
      alignMobile,
      verticalAlign,
      verticalAlignMobile,
      handleChangeSelectedItem,
      handleCloseDrawer,
      handleToggleDeviceMode,
      handleChangeTitle,
      handleChangeSubTitle,
      handleChangeButtons,
      handleSetupSelectedCover,
      handleResetCoverState,
      handleChangeBackground,
      handleChangeBrightness,
      handleChangePublishAt,
      handleChangeButtonsColor,
      handleChangeButtonsSize,
      handleChangeAlign,
      handleChangeAlignMobile,
      handleChangeVerticalAlign,
      handleChangeVerticalAlignMobile,
    ]
  );

  return (
    <CustomCoverContext.Provider value={value}>
      {children}
    </CustomCoverContext.Provider>
  );
};
